import { useState } from 'react';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SettingsIcon from '@mui/icons-material/Settings';
import BugReportIcon from '@mui/icons-material/BugReport';
import MenuIcon from '@mui/icons-material/Menu';
import TokenIcon from '@mui/icons-material/Token';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FolderIcon from '@mui/icons-material/Folder';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useUiStore } from '../../stores/ui.store';
import { useCreditsBalance } from '../../api/queries/credits';
import { useProjects, useProject } from '../../api/queries/projects';
import { useProjectStore } from '../../stores/project.store';
import { useNotifications } from '../../api/queries/notifications';
import { useAuth } from '../../auth/useAuth';
import { useBookStore } from '../../stores/book.store';

interface NavItem {
  to: string;
  label: string;
  icon: typeof DashboardIcon;
  exact?: boolean;
  disabled?: boolean;
}

const LIBRARY_ITEMS: NavItem[] = [
  { to: '/projects', label: 'All Projects', icon: FolderIcon },
  { to: '/series', label: 'Series', icon: CollectionsBookmarkIcon },
  { to: '/entities', label: 'All Entities', icon: PeopleIcon },
  { to: '/worlds', label: 'All Worlds', icon: PublicIcon },
  {
    to: '/reference-library',
    label: 'Reference Library',
    icon: LibraryBooksIcon,
  },
  { to: '/images', label: 'Media Library', icon: PhotoLibraryIcon },
  { to: '/training-queue', label: 'Training Queue', icon: PsychologyIcon },
];

const DEBUG_ITEMS: NavItem[] = [
  { to: '/debug/ai-actions', label: 'AI Actions', icon: BugReportIcon },
];

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

function renderNavItem(
  item: NavItem,
  collapsed: boolean,
  isActive: (to: string, exact?: boolean) => boolean,
) {
  const active = isActive(item.to, item.exact);
  const Icon = item.icon;
  const button = (
    <ListItemButton
      key={item.to}
      component={Link}
      to={item.to}
      selected={active}
      disabled={item.disabled}
      sx={{
        mx: 1,
        borderRadius: 1,
        mb: 0.25,
        minHeight: 44,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1 : 2,
        borderLeft: active ? '3px solid' : '3px solid transparent',
        borderLeftColor: active ? 'primary.main' : 'transparent',
        fontWeight: active ? 700 : 400,
        bgcolor: active ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: active ? 'action.selected' : 'action.hover',
        },
        opacity: item.disabled ? 0.4 : 1,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 36,
          color: active ? 'primary.main' : 'text.secondary',
          justifyContent: 'center',
        }}
      >
        <Icon fontSize="small" />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: active ? 700 : 400,
            noWrap: true,
          }}
        />
      )}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip
      key={item.to}
      title={item.disabled ? `${item.label} (unavailable)` : item.label}
      placement="right"
      arrow
    >
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { data: creditsData } = useCreditsBalance();
  const { data: projects = [] } = useProjects();
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const { data: activeProject } = useProject(activeProjectId ?? undefined);
  const { data: notifications } = useNotifications(true);
  const auth = useAuth();
  const dirty = useBookStore((s) => s.dirty);
  const activeBookId = useBookStore((s) => s.activeBookId);

  const unreadCount = notifications?.length ?? 0;

  // Notification menu state
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const notifOpen = Boolean(notifAnchor);

  // User menu state
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const userOpen = Boolean(userAnchor);

  const isActive = (to: string, exact?: boolean) => {
    if (exact) {
      return !!matchRoute({ to, fuzzy: false });
    }
    return !!matchRoute({ to, fuzzy: true });
  };

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  // Build project-scoped nav items
  const primaryBook = activeProject?.books?.find((b) => b.isPrimary);
  const primaryOutline = activeProject?.outlines?.find((o) => o.isPrimary);

  const projectNavItems: NavItem[] = activeProjectId
    ? [
        {
          to: `/projects/${activeProjectId}`,
          label: 'Dashboard',
          icon: DashboardIcon,
          exact: true,
        },
        {
          to: primaryBook ? `/books/${primaryBook.id}` : '#book-editor',
          label: 'Book Editor',
          icon: MenuBookIcon,
          disabled: !primaryBook,
        },
        {
          to: primaryOutline ? `/outlines/${primaryOutline.id}` : '#outline',
          label: 'Outline',
          icon: AccountTreeIcon,
          disabled: !primaryOutline,
        },
        {
          to: activeProject?.sessionId
            ? `/story-studio/${activeProject.sessionId}`
            : '#story-studio',
          label: 'Story Studio',
          icon: AutoStoriesIcon,
          disabled: !activeProject?.sessionId,
        },
        {
          to: activeProject?.architectId
            ? `/story-architect/${activeProject.architectId}`
            : '#story-architect',
          label: 'Story Architect',
          icon: TimelineIcon,
          disabled: !activeProject?.architectId,
        },
        {
          to: activeProject?.boardId
            ? `/boards/${activeProject.boardId}`
            : '#board',
          label: 'Creativity Board',
          icon: ViewKanbanIcon,
          disabled: !activeProject?.boardId,
        },
        { to: '/worlds', label: 'Worlds', icon: PublicIcon },
        { to: '/entities', label: 'Entities', icon: PeopleIcon },
      ]
    : [];

  const handleProjectChange = (val: string) => {
    setActiveProject(val || null);
    if (val) {
      navigate({ to: '/projects/$projectId', params: { projectId: val } });
    }
  };

  return (
    <Box
      component="aside"
      sx={{
        width,
        minWidth: width,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        transition: 'width 200ms ease, min-width 200ms ease',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 1 : 2,
          py: 1.5,
          minHeight: 56,
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ letterSpacing: -0.3 }}
          >
            Creator Platform
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Project Selector */}
      {!collapsed ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Filterable + alphabetically sorted per project-wide dropdown convention. */}
          <FilterableSelect
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            value={activeProjectId ?? ''}
            onChange={handleProjectChange}
            placeholder="Select a project..."
            emptyLabel="Select a project..."
            size="small"
            fullWidth
          />
          <Button
            size="small"
            onClick={() => navigate({ to: '/projects' })}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Manage Projects
          </Button>
        </Box>
      ) : (
        <Tooltip title="Projects" placement="right" arrow>
          <IconButton
            size="small"
            sx={{ mx: 'auto', my: 1 }}
            onClick={() => navigate({ to: '/projects' })}
          >
            <FolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Divider />

      {/* Main nav */}
      <List sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        {/* Project-scoped nav */}
        {activeProjectId && projectNavItems.length > 0 && (
          <>
            {!collapsed && (
              <ListSubheader
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  lineHeight: '32px',
                  bgcolor: 'transparent',
                }}
              >
                Project
              </ListSubheader>
            )}
            {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
            {projectNavItems.map((item) =>
              renderNavItem(item, collapsed, isActive),
            )}
          </>
        )}

        {!activeProjectId && (
          <>
            {!collapsed && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Select or create a project to see project tools.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Library section */}
        {!collapsed && (
          <ListSubheader
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              lineHeight: '32px',
              bgcolor: 'transparent',
              mt: 1,
            }}
          >
            Library
          </ListSubheader>
        )}
        {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {LIBRARY_ITEMS.map((item) => renderNavItem(item, collapsed, isActive))}

        {/* Notifications */}
        {!collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {(() => {
          const notifButton = (
            <ListItemButton
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 0.25,
                minHeight: 44,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2,
                borderLeft: '3px solid transparent',
              }}
              onClick={(e) => setNotifAnchor(e.currentTarget)}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: 'text.secondary',
                  justifyContent: 'center',
                }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary="Notifications"
                  primaryTypographyProps={{
                    fontSize: 14,
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          );
          return collapsed ? (
            <Tooltip title="Notifications" placement="right" arrow>
              <span>{notifButton}</span>
            </Tooltip>
          ) : (
            notifButton
          );
        })()}
        <Menu
          anchorEl={notifAnchor}
          open={notifOpen}
          onClose={() => setNotifAnchor(null)}
          transformOrigin={{ horizontal: 'left', vertical: 'center' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
          slotProps={{ paper: { sx: { width: 320, maxHeight: 400 } } }}
        >
          {notifications && notifications.length > 0 ? (
            notifications.slice(0, 10).map((n) => (
              <MenuItem key={n.id} onClick={() => setNotifAnchor(null)}>
                <ListItemText
                  primary={n.title}
                  secondary={n.message}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                  secondaryTypographyProps={{
                    fontSize: 12,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText
                primary="No new notifications"
                primaryTypographyProps={{
                  fontSize: 13,
                  color: 'text.secondary',
                }}
              />
            </MenuItem>
          )}
        </Menu>

        {/* Settings */}
        {renderNavItem(
          { to: '/settings', label: 'Settings', icon: SettingsIcon },
          collapsed,
          isActive,
        )}
      </List>

      {/* Debug section */}
      <Divider />
      <List dense sx={{ py: 0.5 }}>
        {DEBUG_ITEMS.map((item) => {
          const active = isActive(item.to);
          const Icon = item.icon;
          const button = (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              selected={active}
              sx={{
                mx: 1,
                borderRadius: 1,
                minHeight: 40,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: 'text.secondary',
                  justifyContent: 'center',
                }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                />
              )}
            </ListItemButton>
          );
          return collapsed ? (
            <Tooltip key={item.to} title={item.label} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </List>

      {/* Footer — Autosave + Credits + User Profile */}
      <Divider />
      <Box
        sx={{
          px: collapsed ? 1 : 2,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        {/* Autosave indicator */}
        {activeBookId &&
          (collapsed ? (
            <Tooltip
              title={dirty ? 'Saving...' : 'Saved'}
              placement="right"
              arrow
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {dirty ? (
                  <SyncIcon
                    fontSize="small"
                    color="warning"
                    sx={{
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                ) : (
                  <CheckCircleIcon fontSize="small" color="success" />
                )}
              </Box>
            </Tooltip>
          ) : (
            <Chip
              size="small"
              icon={
                dirty ? (
                  <SyncIcon
                    fontSize="small"
                    sx={{
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                ) : (
                  <CheckCircleIcon fontSize="small" />
                )
              }
              label={dirty ? 'Saving...' : 'Saved'}
              color={dirty ? 'warning' : 'success'}
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            />
          ))}

        {/* Credits */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 1,
          }}
        >
          <TokenIcon fontSize="small" sx={{ color: 'warning.main' }} />
          {!collapsed && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {creditsData?.balance != null
                ? `${creditsData.balance.toLocaleString()} credits`
                : '-- credits'}
            </Typography>
          )}
        </Box>

        {/* User profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 1,
            cursor: 'pointer',
            borderRadius: 1,
            py: 0.5,
            px: collapsed ? 0 : 0.5,
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={(e) => setUserAnchor(e.currentTarget)}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: 13,
              bgcolor: 'primary.main',
            }}
          >
            {auth.email?.charAt(0).toUpperCase() ?? 'U'}
          </Avatar>
          {!collapsed && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ flex: 1 }}
            >
              {auth.email}
            </Typography>
          )}
        </Box>
        <Menu
          anchorEl={userAnchor}
          open={userOpen}
          onClose={() => setUserAnchor(null)}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            component={Link}
            to="/settings"
            onClick={() => setUserAnchor(null)}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserAnchor(null);
              auth.signOut();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
